import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import {
  requiredArrayValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import RippleButton from "../../components/common/RippleButton";
import ShadowCard from "../../components/common/ShadowCard";
import { getAllActiveCities } from "../city/services/city.services";
import {
  createHotel,
  getAllHotelContacts,
  getAllHotelRoomCategory,
  getHotelByID,
  updateHotel,
} from "./services/hotel.services";
import { clearHotelDetails } from "./hotelSlice";
import { ICity } from "../city/cityModel";
import { findIndex } from "lodash";
import { IState } from "../state/stateModel";
import { ICountry } from "../country/countryModel";
import {
  CALENDERDAYS,
  // STARRATING,
  STATUSARRAY,
} from "../../_contstants/common";
import { getAllActiveStates } from "../state/services/state.services";
import { getAllActiveCountries } from "../country/services/country.services";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormCheckboxGroup from "../../components/formFields/FormCheckboxGroup";
import CreateHotelContact from "./CreateHotelContact";
import ButtonForAll from "../../components/common/ButtonForAll";
import CreateHotelRoomCategory from "./CreateHotelRoomCategory";
import CreateHotelSpecialRate from "./CreateHotelSpecialRate";
import FormRatingField from "../../components/formFields/FormRatingField";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import CreateHotelImages from "./CreateHotelImages";
import { getAllActiveSectors } from "../sector/services/sector.services";
import { ISector } from "../sector/sectorModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { slugify } from "../../_helper/helper";
import FormTimePicker from "../../components/formFields/FormTimeField";
import moment from "moment";

interface CityChangeWatcherProps {
  formRenderProps: FormRenderProps;
  CityList: ICity[];
}

const CityChangeWatcher: React.FC<CityChangeWatcherProps> = ({
  CityList,
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");

  useEffect(() => {
    const CityID = cityID ? cityID : undefined;
    const cityIndex = findIndex(CityList, { ID: CityID });
    if (cityIndex > -1) {
      const city = CityList[cityIndex];
      formRenderProps.onChange("StateID", { value: city.StateID });
      formRenderProps.onChange("CountryID", { value: city.CountryID });
    } else {
      formRenderProps.onChange("CityID", { value: "" });
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CountryID", { value: "" });
    }
  }, [cityID]);
  return null;
};

const CreateHotel: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const HotelID = location.state?.HotelID;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.hotel.loading);
  const hotelImageLoading = useAppSelector(
    (state) => state.hotel.hotelImageLoading,
  );
  const hotelContactLoading = useAppSelector(
    (state) => state.hotel.hotelContactLoading,
  );
  const hotelRoomCategoryLoading = useAppSelector(
    (state) => state.hotel.hotelRoomCategoryLoading,
  );
  const hotelSpecialRateLoading = useAppSelector(
    (state) => state.hotel.hotelSpecialRateLoading,
  );
  const HotelDetail = useAppSelector((state) => state.hotel.HotelDetail);
  const CityList = useAppSelector((state) => state.city.CityList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const CountryList = useAppSelector((state) => state.country.CountryList);
  const SectorList = useAppSelector((state) => state.sector.SectorList);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [HotelDetail, CityList]);

  useEffect(() => {
    if (HotelID) {
      dispatch(getHotelByID(HotelID));
      dispatch(getAllHotelContacts({ HotelID: HotelID }));
      dispatch(getAllHotelRoomCategory({ HotelID: HotelID }));
    }
    return () => {
      dispatch(clearHotelDetails());
    };
  }, [HotelID]);

  useEffect(() => {
    dispatch(getAllActiveCities());
    dispatch(getAllActiveStates());
    dispatch(getAllActiveCountries());
    dispatch(getAllActiveSectors());
  }, []);

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e?.value);
    formRenderProps.onChange("URL", { value: slug });
  };

  const handleSubmit = async (values: any) => {
    if (HotelID) {
      const editPayload = {
        ID: values.ID,
        HotelName: values?.HotelName ? values?.HotelName : "",
        Address: values?.Address ? values?.Address : "",
        CityID: values?.CityID ? +values?.CityID : null,
        StateID: values?.StateID ? +values?.StateID : null,
        CountryID: values?.CountryID ? +values?.CountryID : null,
        HotelCategory: values?.HotelCategory ? `${values?.HotelCategory}` : "",
        WeekDays: values?.WeekDays ? values?.WeekDays?.join(", ") : "",
        WeekEnds: values?.WeekEnds ? values?.WeekEnds?.join(", ") : "",
        IsActive: values?.IsActive === 1,

        HotelDescription: values?.HotelDescription
          ? values?.HotelDescription
          : "",
        BookingURL: values?.BookingURL ? values?.BookingURL : "",
        CheckInTime: values?.CheckInTime
          ? moment(values?.CheckInTime)?.format("hh:mm A")
          : "",
        CheckOutTime: values?.CheckOutTime
          ? moment(values?.CheckOutTime).format("hh:mm A")
          : "",

        SequenceNo: values?.SequenceNo ? values?.SequenceNo : 0,
        PurchaseRate: values?.PurchaseRate ? values?.PurchaseRate : null,
        Notes: values?.Notes ? values?.Notes : "",
        CancellationPolicy: values?.CancellationPolicy
          ? values?.CancellationPolicy
          : "",
        URL: values?.URL ? values?.URL : "",
        Bedroom: values?.Bedroom ? values?.Bedroom : "",
        LivingArea: values?.LivingArea ? values?.LivingArea : "",
        RoomAminities: values?.RoomAminities ? values?.RoomAminities : "",
        BathroomFacilities: values?.BathroomFacilities
          ? values?.BathroomFacilities
          : "",
        SectorID: values?.SectorID ? +values?.SectorID : null,
        TotalRoom: values?.TotalRoom ? +values?.TotalRoom : null,
        IsDisplay: values?.IsDisplay ? values?.IsDisplay : false,
      };
      try {
        const response = await dispatch(updateHotel(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(clearHotelDetails());
          navigate("/hotel");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    } else {
      const insertPayload = {
        HotelName: values?.HotelName ? values?.HotelName : "",
        Address: values?.Address ? values?.Address : "",
        CityID: values?.CityID ? +values?.CityID : null,
        StateID: values?.StateID ? +values?.StateID : null,
        CountryID: values?.CountryID ? +values?.CountryID : null,
        HotelCategory: values?.HotelCategory ? `${values?.HotelCategory}` : "",
        WeekDays: values?.WeekDays ? values?.WeekDays?.join(", ") : "",
        WeekEnds: values?.WeekEnds ? values?.WeekEnds?.join(", ") : "",
        IsActive: values?.IsActive === 1,

        HotelDescription: values?.HotelDescription
          ? values?.HotelDescription
          : "",
        BookingURL: values?.BookingURL ? values?.BookingURL : "",
        CheckInTime: values?.CheckInTime
          ? moment(values?.CheckInTime)?.format("hh:mm A")
          : "",
        CheckOutTime: values?.CheckOutTime
          ? moment(values?.CheckOutTime)?.format("hh:mm A")
          : "",
        SequenceNo: values?.SequenceNo ? values?.SequenceNo : 0,
        PurchaseRate: values?.PurchaseRate ? values?.PurchaseRate : null,
        Notes: values?.Notes ? values?.Notes : "",
        CancellationPolicy: values?.CancellationPolicy
          ? values?.CancellationPolicy
          : "",
        URL: values?.URL ? values?.URL : "",
        Bedroom: values?.Bedroom ? values?.Bedroom : "",
        LivingArea: values?.LivingArea ? values?.LivingArea : "",
        RoomAminities: values?.RoomAminities ? values?.RoomAminities : "",
        BathroomFacilities: values?.BathroomFacilities
          ? values?.BathroomFacilities
          : "",
        SectorID: values?.SectorID ? +values?.SectorID : null,
        TotalRoom: values?.TotalRoom ? +values?.TotalRoom : null,
        IsDisplay: values?.IsDisplay ? values?.IsDisplay : false,
      };
      try {
        const response = await dispatch(createHotel(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          // dispatch(clearHotelDetails());
          // navigate("/hotel");
          navigate(`/hotel/edit`, {
            state: {
              HotelID: response.payload.Data?.HotelID,
            },
          });
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  return (
    <>
      {(loading ||
        hotelImageLoading ||
        hotelContactLoading ||
        hotelRoomCategoryLoading ||
        hotelSpecialRateLoading) && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={HotelDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                <GridLayoutItem colSpan={4}>
                  <Typography.h4>
                    {HotelID ? "Update Hotel" : "Add Hotel"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="HotelName"
                    label="Hotel Name"
                    placeholder="Hotel Name"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                    onChange={(e) => handleCreateSlug(e, formRenderProps)}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    name="Address"
                    label="Address"
                    placeholder="Address"
                    component={FormTextArea}
                    astrike={true}
                    validator={requiredValidator}
                    rows={1}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="HotelCategory"
                    component={FormRatingField}
                    label="Hotel Category"
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CityID"}
                    name={"CityID"}
                    label={"City"}
                    placeholder="i.e. Ahmedabad"
                    component={FormSelectionField}
                    astrike={true}
                    validator={requiredValidator}
                    options={CityList?.map((city: ICity) => {
                      return {
                        value: city?.ID,
                        label: city?.CityName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"StateID"}
                    name={"StateID"}
                    label={"State"}
                    placeholder="i.e. Gujarat"
                    component={FormSelectionField}
                    disabled={true}
                    // validator={requiredValidator}
                    options={StateList?.map((state: IState) => {
                      return {
                        value: state?.ID,
                        label: state?.StateName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CountryID"}
                    name={"CountryID"}
                    label={"Country"}
                    placeholder="i.e. India"
                    component={FormSelectionField}
                    disabled={true}
                    // validator={requiredValidator}
                    options={CountryList?.map((country: ICountry) => {
                      return {
                        value: country?.ID,
                        label: country?.CountryName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"SectorID"}
                    name={"SectorID"}
                    label={"Sector"}
                    placeholder="Sector"
                    component={FormSelectionField}
                    astrike={true}
                    validator={requiredValidator}
                    options={SectorList?.map((sector: ISector) => {
                      return {
                        value: sector?.ID,
                        label: sector?.SectorName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="URL"
                    label="URL"
                    placeholder="URL"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                    onInput={(e: any) => {
                      e.target.value = slugify(e?.target?.value);
                    }}
                    onBlurCapture={(e: any) => {
                      if (e?.target?.value) {
                        formRenderProps.onChange("URL", {
                          value: slugify(e?.target?.value)?.replace(/-+$/, ""),
                        });
                      }
                    }}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="BookingURL"
                    label="Booking URL"
                    placeholder="Booking URL"
                    component={FormTextField}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="SequenceNo"
                    label="Sequence No"
                    placeholder="0"
                    component={FormNumericTextField}
                    format="n0"
                    min={0}
                    max={1000}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="w-100"
                    name={"CheckInTime"}
                    label={"Check-in Time"}
                    component={FormTimePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="w-100"
                    name={"CheckOutTime"}
                    label={"Check-out Time"}
                    component={FormTimePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                {/* <GridLayoutItem>
                  <Field
                    name="PurchaseRate"
                    label="Purchase Rate"
                    placeholder="Purchase Rate"
                    type="number"
                    component={FormTextField}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem> */}
                <GridLayoutItem colSpan={4}>
                  <Field
                    name="HotelDescription"
                    label="Hotel Description"
                    placeholder="Hotel Description"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Notes"
                    label="Notes"
                    placeholder="Notes"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="CancellationPolicy"
                    label="Cancellation Policy"
                    placeholder="Cancellation Policy"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={4}>
                  <Field
                    id={"WeekDays"}
                    name={"WeekDays"}
                    title={"Week Days"}
                    marginRight={50}
                    marginBeetween={8}
                    validator={requiredArrayValidator}
                    component={FormCheckboxGroup}
                    options={CALENDERDAYS}
                    disabledOptions={formRenderProps.valueGetter("WeekEnds")}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={4}>
                  <Field
                    id={"WeekEnds"}
                    name={"WeekEnds"}
                    title={"Week Ends"}
                    marginRight={50}
                    marginBeetween={8}
                    component={FormCheckboxGroup}
                    options={CALENDERDAYS}
                    disabledOptions={formRenderProps.valueGetter("WeekDays")}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    width: "100%",
                    margin: "18px 0px 0px",
                    padding: "0px 10px 8px",
                    fontWeight: 700,
                    background: "whitesmoke",
                    borderRadius: 5,
                    height: 48,
                  }}
                >
                  <Field
                    name="IsDisplay"
                    label="Is Display"
                    component={FormCheckbox}
                    wrapperStyle={{ fontWeight: 600 }}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="TotalRoom"
                    label="Total Room"
                    placeholder="0"
                    format="n0"
                    max={100}
                    component={FormNumericTextField}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="w-100"
                    name="IsActive"
                    placeholder="i.e. Active"
                    label="Status"
                    astrike={true}
                    validator={requiredValidator}
                    component={FormSelectionField}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="BathroomFacilities"
                    label="Bathroom Facilities"
                    placeholder="Bathroom Facilities"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="RoomAminities"
                    label="Room Aminities"
                    placeholder="Room Aminities"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="LivingArea"
                    label="Living Area"
                    placeholder="Living Area"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Bedroom"
                    label="Bedroom"
                    placeholder="Bedroom"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={4}
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                    marginTop: 20,
                  }}
                >
                  <ButtonForAll
                    label={HotelID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                  />

                  <RippleButton
                    type="button"
                    fillMode="outline"
                    themeColor="primary"
                    style={{ marginLeft: 10 }}
                    onClick={() => navigate("/hotel")}
                  >
                    Cancel
                  </RippleButton>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <CityChangeWatcher
              formRenderProps={formRenderProps}
              CityList={CityList}
            />
          </FormElement>
        )}
      />
      {HotelID && (
        <>
          <CreateHotelImages />
          <CreateHotelContact />
          <CreateHotelRoomCategory />
          <CreateHotelSpecialRate />
        </>
      )}
    </>
  );
};

export default CreateHotel;
